import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import './index.css';

// Polyfill ResizeObserver pour Safari < 13.1
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';
if (typeof ResizeObserver === 'undefined') {
  (window as any).ResizeObserver = ResizeObserverPolyfill;
}
import { ThemeProvider } from './ui/components/ThemeProvider';
// Initialize i18n
import './i18n';
import { registerSW } from 'virtual:pwa-register';
import { Capacitor } from '@capacitor/core';

// Patch pour éviter les [object Object] dans Logcat sur Android/iOS
if (Capacitor.isNativePlatform()) {
  const patchConsole = (method: 'log' | 'error' | 'warn' | 'info') => {
    const original = (console as any)[method];
    (console as any)[method] = (...args: any[]) => {
      const serialized = args.map(arg => {
        if (arg instanceof Error) {
          return JSON.stringify({
            message: arg.message,
            stack: arg.stack,
            name: arg.name
          });
        }
        if (typeof arg === 'object' && arg !== null) {
          try {
            return JSON.stringify(arg);
          } catch (e) {
            // Fallback pour les références circulaires ou objets complexes
            const keys = Object.keys(arg);
            return `[Object (${keys.length} keys): ${keys.slice(0, 5).join(', ')}${keys.length > 5 ? '...' : ''}]`;
          }
        }
        return arg;
      });
      original.apply(console, serialized);
    };
  };
  patchConsole('log');
  patchConsole('error');
  patchConsole('warn');
  patchConsole('info');
}

// Gérer le Service Worker
if (Capacitor.isNativePlatform()) {
  // Sur application Native (Android/iOS), on force la suppression du Service Worker
  // car l'applitcation n'en a pas besoin (les fichiers sont déjà en local)
  // Cela empêche le WebView de rester bloqué sur une ancienne version en cache.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) {
        registration.unregister();
      }
    });
  }
} else {
  // Register Service Worker for PWA - Only on web.
  // Le rechargement après déploiement est géré automatiquement par vite-plugin-pwa
  // (registerType: 'autoUpdate'). On n'ajoute donc PAS de listener 'controllerchange'
  // manuel : il serait redondant avec autoUpdate et multiplierait les rechargements.
  registerSW({ immediate: false });
}

// Sources de bruit Sentry : scripts injectés par les navigateurs in-app (Facebook,
// Instagram, TikTok) et par les extensions. Aucune de nos erreurs réelles ne provient
// de ces URLs (le code applicatif est servi depuis /assets/*.js).
const SENTRY_DENY_URLS = [
  /^iabjs:\/\//i,
  /webviewprogressproxy/i,
  /graph\.facebook\.com/i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
];

// Messages d'erreur sans valeur de diagnostic (ponts JS<->natif détruits en fin de
// page, boucles ResizeObserver, rejets non-Error des scripts tiers).
const SENTRY_IGNORE_ERRORS = [
  /Java object is gone/i,
  /Java exception was raised during method invocation/i,
  /ResizeObserver loop/i,
  'Non-Error promise rejection captured',
];

// Initialisation de Sentry Frontend (différée pour optimiser le LCP)
const initSentry = () => {
  if ((import.meta as any).env.PROD && (import.meta as any).env.VITE_SENTRY_DSN) {
    const integrations: any[] = [
      Sentry.browserTracingIntegration(),
    ];

    // Désactiver Replay sur Mobile pour gagner en performance (LCP/TBT)
    if (!Capacitor.isNativePlatform() && window.innerWidth > 1024) {
      integrations.push(Sentry.replayIntegration());
    }

    Sentry.init({
      dsn: (import.meta as any).env.VITE_SENTRY_DSN,
      integrations,
      // Bruit des WebViews in-app et des extensions : ces erreurs proviennent de
      // scripts injectés (Facebook/Instagram, TikTok, extensions), jamais de notre code.
      denyUrls: SENTRY_DENY_URLS,
      ignoreErrors: SENTRY_IGNORE_ERRORS,
      // Performance Monitoring
      tracesSampleRate: 0.1, 
      // Session Replay
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
    });
  }
};

// Lancer Sentry après le premier rendu ou quand le navigateur est inactif
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => initSentry(), { timeout: 3000 });
  } else {
    setTimeout(initSentry, 2000);
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

// Balises SEO statiques (index.html et pages de dist/_seo, cf. scripts/prerenderSeo.mjs) :
// le <SEO> de chaque page prend le relais, et React 19 ne déduplique pas <title>/<meta>.
document.querySelectorAll('[data-prerender]').forEach(node => node.remove());

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ThemeProvider>
      <App />
    </ThemeProvider>
  </React.StrictMode>
);
